import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Card, Input, Label, Select, Textarea } from "./_shared";

export function DealsTab() {
  const { apiFetch } = useAuth();
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try { const d = await apiFetch("/api/admin/deals"); setList(d.items || []); }
    catch (e) { setErr(e?.message || "Failed to load"); }
  }, [apiFetch]);
  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    await apiFetch("/api/admin/deals", { method: "POST", body: JSON.stringify(payload) });
    setShowForm(false);
    await load();
  };
  const toggle = async (deal) => {
    await apiFetch(`/api/admin/deals/${deal.id}`, { method: "PATCH", body: JSON.stringify({ is_active: !deal.is_active }) });
    await load();
  };
  const remove = async (deal) => {
    if (!window.confirm(`Slette tilbudet "${deal.name}"? Stripe-coupon-en deaktiveres ikke automatisk.`)) return;
    await apiFetch(`/api/admin/deals/${deal.id}`, { method: "DELETE" });
    await load();
  };

  const now = new Date();
  const status = (d) => {
    if (!d.is_active) return { label: "Inactive", color: "#71717A" };
    const starts = new Date(d.starts_at), ends = new Date(d.ends_at);
    if (now < starts) return { label: "Scheduled", color: "#0B6CF5" };
    if (now > ends)   return { label: "Expired",   color: "#71717A" };
    return { label: "Live", color: "#10B981" };
  };

  return (
    <div className="space-y-4" data-testid="deals-root">
      <Card title="DEALS · PROMOTIONAL DISCOUNTS"
            subtitle="Run a time-bounded discount. Stripe coupon is auto-created; the landing page banner displays the reason; checkout auto-applies the discount."
            accent="#FF8A5C" testid="deals-card">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <div className="text-[11px] text-[var(--srs-ink-dim)]">
            {list?.length || 0} {list?.length === 1 ? "deal" : "deals"} total
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]" data-testid="deals-refresh">Refresh</button>
            <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF8A5C] text-white text-[10px] font-bold tracking-[0.2em] uppercase rounded-full" data-testid="deals-new">
              <Plus className="w-3 h-3" /> New deal
            </button>
          </div>
        </div>

        {err && <div className="text-xs text-[#EF4444] mb-2">{err}</div>}
        {list === null && <div className="text-xs text-[var(--srs-ink-faint)]">Loading…</div>}
        {list?.length === 0 && (
          <div className="text-xs text-[var(--srs-ink-faint)] py-8 text-center" data-testid="deals-empty">
            No deals yet. Hit "New deal" to launch your first promo.
          </div>
        )}

        <div className="flex flex-col gap-2">
          {list?.map((d) => {
            const st = status(d);
            return (
              <div key={d.id} data-testid={`deal-row-${d.id}`} className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 rounded-lg">
                <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[var(--srs-ink)] text-base">{d.name}</span>
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase"
                          style={{ background: `${st.color}1F`, color: st.color, border: `1px solid ${st.color}59` }}>
                      {st.label}
                    </span>
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase bg-[#FF8A5C] text-white">
                      –{d.percent_off}%
                    </span>
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase bg-[var(--srs-surface-2)] text-[var(--srs-ink-dim)] border border-[var(--srs-line)]">
                      {d.applies_to}
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => toggle(d)} data-testid={`deal-toggle-${d.id}`}
                            className="px-2 py-1 text-[10px] tracking-[0.15em] uppercase font-bold border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]">
                      {d.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={() => remove(d)} data-testid={`deal-delete-${d.id}`}
                            className="px-2 py-1 text-[10px] tracking-[0.15em] uppercase font-bold border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[#EF4444] hover:border-[#EF4444]">
                      <Trash2 className="w-3 h-3 inline" />
                    </button>
                  </div>
                </div>
                <div className="text-[13px] text-[var(--srs-ink)] mb-1">{d.reason}</div>
                <div className="text-[11px] text-[var(--srs-ink-faint)] font-mono">
                  {new Date(d.starts_at).toLocaleDateString("nb-NO")} → {new Date(d.ends_at).toLocaleDateString("nb-NO")}
                  · coupon: {d.stripe_coupon_id || "—"}
                </div>
              </div>
            );
          })}
        </div>

        {showForm && <DealFormModal onClose={() => setShowForm(false)} onCreate={create} />}
      </Card>
    </div>
  );
}

export function DealFormModal({ onClose, onCreate }) {
  const today = new Date();
  const inSevenDays = new Date(today.getTime() + 7 * 86400000);
  const fmtLocal = (d) => d.toISOString().slice(0, 16);  // YYYY-MM-DDTHH:MM for <input type=datetime-local>

  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [percent, setPercent] = useState(20);
  const [appliesTo, setAppliesTo] = useState("both");
  const [startsAt, setStartsAt] = useState(fmtLocal(today));
  const [endsAt, setEndsAt] = useState(fmtLocal(inSevenDays));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !reason.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      await onCreate({
        name: name.trim(),
        reason: reason.trim(),
        percent_off: Number(percent),
        applies_to: appliesTo,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        is_active: true,
      });
    } catch (e) { setErr(e?.message || "Create failed"); setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="deal-form-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(11,36,64,0.55)", backdropFilter: "blur(8px)", display: "grid", placeItems: "center", padding: 24, zIndex: 50 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            style={{ width: "100%", maxWidth: 540, background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 16, padding: 28 }}>
        <div className="srs-mono text-[10px] tracking-[0.3em] text-[#FF8A5C] font-bold mb-1">// NEW DEAL</div>
        <h2 className="text-xl font-semibold mb-4 text-[var(--srs-ink)]">Launch a discount</h2>

        <Label>Name (internal)</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Black Friday 2026" data-testid="deal-name" maxLength={120} required />

        <Label>Reason (public banner text)</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Vi feirer Black Friday med rabatt på EuroPilot" data-testid="deal-reason" rows={2} maxLength={300} required />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Percent off</Label>
            <Input type="number" min={1} max={90} value={percent} onChange={(e) => setPercent(e.target.value)} data-testid="deal-percent" required />
          </div>
          <div>
            <Label>Applies to</Label>
            <Select value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)} data-testid="deal-applies">
              <option value="both">Both (monthly + yearly)</option>
              <option value="monthly">Monthly only</option>
              <option value="yearly">Yearly only</option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Starts</Label>
            <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} data-testid="deal-starts" required />
          </div>
          <div>
            <Label>Ends</Label>
            <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="deal-ends" required />
          </div>
        </div>

        {err && <div className="text-xs text-[#EF4444] mt-2">{err}</div>}

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] rounded-full">Cancel</button>
          <button type="submit" disabled={busy || !name.trim() || !reason.trim()}
                  data-testid="deal-submit"
                  className="inline-flex items-center gap-2 px-5 py-2 bg-[#FF8A5C] text-white text-xs font-bold tracking-[0.18em] uppercase rounded-full disabled:opacity-50">
            {busy ? "Creating…" : "Launch deal"}
          </button>
        </div>
      </form>
    </div>
  );
}

